import { useEffect, useState } from 'react';

/**
 * Service worker registration, and the one piece of UI it needs.
 *
 * The update prompt is not decoration. A cached shell against a redeployed API
 * is the failure mode that makes an installed PWA worse than a website: the
 * worker keeps a months-old app that talks to endpoints which have moved, and
 * has no way to know why nothing works. The worker never calls `skipWaiting()`
 * on its own — it waits to be told, so an update cannot swap the app out from
 * under someone mid-consent.
 */

const SW_URL = '/sw.js';

let waiting: ServiceWorker | null = null;
const listeners = new Set<(has: boolean) => void>();

function announce(has: boolean) {
  for (const l of listeners) l(has);
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  // Registered after load so precaching competes with nothing on the first
  // paint — this app's first visit is often on a 3G connection.
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(SW_URL, { scope: '/' }).then((reg) => {
      if (reg.waiting) { waiting = reg.waiting; announce(true); }

      reg.addEventListener('updatefound', () => {
        const next = reg.installing;
        if (!next) return;
        next.addEventListener('statechange', () => {
          // `controller` distinguishes an update from the very first install.
          // Prompting on first install would ask a worker to reload an app
          // they have only just opened.
          if (next.state === 'installed' && navigator.serviceWorker.controller) {
            waiting = next;
            announce(true);
          }
        });
      });
    }).catch(() => {
      // No service worker means no offline shell, and nothing else. The app
      // works; it just has to be online. Not worth a message to the worker.
    });
  });

  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
}

/**
 * Clears everything this origin holds for the previous session.
 *
 * Called on sign-out, and deliberately not awaited on the network: docs/14 is
 * explicit that a worker handing their phone to someone else must never be
 * told "log out failed, try again online". The IndexedDB store is destroyed by
 * the auth layer; this clears the shell caches alongside it.
 */
export async function purgeCaches(): Promise<void> {
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: 'PURGE' });
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch {
    // A cache that will not clear must not block sign-out.
  }
}

/**
 * The prompt. One line, one action, dismissible — an update banner a worker
 * cannot dismiss is a banner they learn to ignore.
 */
export function UpdatePrompt() {
  const [has, setHas] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    listeners.add(setHas);
    return () => { listeners.delete(setHas); };
  }, []);

  if (!has || dismissed) return null;

  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-16 z-20 mx-auto flex max-w-md items-center gap-3
                 border-t border-primary-200 bg-primary-50 px-4 py-3"
    >
      <p className="min-w-0 flex-1 text-sm text-neutral-800">
        A new version of the app is ready.
      </p>
      <button
        onClick={() => setDismissed(true)}
        className="min-h-11 shrink-0 rounded-lg px-3 text-sm text-neutral-700 active:bg-neutral-100"
      >
        Later
      </button>
      <button
        onClick={() => waiting?.postMessage({ type: 'SKIP_WAITING' })}
        className="min-h-11 shrink-0 rounded-lg bg-primary-600 px-4 text-sm font-medium text-white active:bg-primary-700"
      >
        Update
      </button>
    </div>
  );
}
